import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import {
    DocumentDetailsDrawer, FolderManagement, FolderNavigation, MyDocumentsSection,
    OutsideWorkspaceDocumentsSection
} from '@/components/knowledge-base';
import { BulkActionToolbar } from '@/components/knowledge-base/BulkActionToolbar';
import {
    DesktopFilterControls, MobileFilterSheet
} from '@/components/knowledge-base/DocumentFilterControls';
import { Button } from '@/components/ui/button';
import {
    useBulkDeleteDocuments, useBulkUpdateDocuments,
    useDeleteDocument, useRemoveDocumentFromKB, useSendDocumentToElevenLabs, useUpdateDocument,
    useConsolidateDocuments
} from '@/hooks/documents';
import { useDocumentSelection } from '@/hooks/documents/useDocumentSelection';
import { usePaginatedDocuments, useDocumentsOutsideWorkspace } from '@/hooks/documents/useDocumentFetch';
import { useKnowledgeBaseToggle } from '@/hooks/documents/useKnowledgeBaseToggle';
import { useFolders } from '@/hooks/folders/useFolders';
import { cn } from '@/lib/utils';
import { Agent } from '@/types/agent/agent';
import { DocumentWithMetadata } from '@/types/knowledge/knowledge-base';

interface KnowledgeBaseTabProps {
  agents: Partial<Agent>[];
  userId?: string;
  searchTerm?: string;
  onRequestUpload?: () => void;
  /**
   * Narrows the list to one topic cluster picked in the brain overview above.
   * Applied client-side over the already-fetched page — clusters are computed
   * in the browser, so there is no server-side filter to hand this to.
   */
  clusterFilter?: { label: string; documentIds: string[] } | null;
  onClearClusterFilter?: () => void;
  /**
   * Hands a document to this tab's existing details drawer. Lets the review
   * queue's "Update" action reuse the drawer instead of standing up a second one.
   */
  documentToOpen?: DocumentWithMetadata | null;
  onDocumentOpened?: () => void;
}

const KnowledgeBaseTab = ({
  agents,
  userId,
  searchTerm,
  onRequestUpload,
  clusterFilter,
  onClearClusterFilter,
  documentToOpen,
  onDocumentOpened,
}: KnowledgeBaseTabProps) => {
  const [selectedDocument, setSelectedDocument] = useState<DocumentWithMetadata | null>(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [isDetailsReadOnly, setIsDetailsReadOnly] = useState(false);
  const [selectedGenieId, setSelectedGenieId] = useState<string>("all");
  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(undefined);
  const [selectedSourceType, setSelectedSourceType] = useState<string | undefined>(undefined);
  const [isFoldersCollapsed, setIsFoldersCollapsed] = useState(false);
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = usePaginatedDocuments(
    "my-documents",
    50,
    {
      folderId: selectedFolderId,
      agentId: selectedGenieId,
      selectedSourceType,
      searchTerm: searchTerm || undefined,
    },
    userId,
  );
  const { data: outsideWorkspaceDocuments = [], isLoading: isLoadingOutsideWorkspaceDocuments } = useDocumentsOutsideWorkspace();

  const allDocuments = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) || [];
  }, [data]);

  const documents = useMemo(() => {
    if (!clusterFilter) return allDocuments;
    const ids = new Set(clusterFilter.documentIds);
    return allDocuments.filter((doc) => ids.has(doc.id));
  }, [allDocuments, clusterFilter]);

  const { mutate: deleteDocument } = useDeleteDocument();
  const { mutateAsync: updateDocument } = useUpdateDocument();
  const sendToElevenLabs = useSendDocumentToElevenLabs();
  const removeFromKB = useRemoveDocumentFromKB();

  // Bulk operations
  const selection = useDocumentSelection();
  const { mutateAsync: bulkDelete, isPending: isBulkDeleting } = useBulkDeleteDocuments();
  const { mutateAsync: bulkUpdate, isPending: isBulkUpdating } = useBulkUpdateDocuments();
  const { mutateAsync: consolidate, isPending: isConsolidating } = useConsolidateDocuments();
  const { moveDocumentsToFolder, isMoving: isFolderMoving } = useFolders();
  const { toggleDocuments } = useKnowledgeBaseToggle();

  const documentIds = useMemo(() => documents.map((d) => d.id), [documents]);

  const handleBulkMoveToFolder = async (folderId: string | null) => {
    const ids = Array.from(selection.selectedIds);
    if (folderId) {
      moveDocumentsToFolder({ documentIds: ids, folderId });
    } else {
      await bulkUpdate({ documentIds: ids, updates: { folder_id: null } });
    }
    selection.deselectAll();
  };

  const handleBulkRemoveFromFolder = async () => {
    const ids = Array.from(selection.selectedIds);
    await bulkUpdate({ documentIds: ids, updates: { folder_id: null } });
    selection.deselectAll();
  };

  const handleBulkAssignToGenie = async (agent: Partial<Agent>) => {
    const selectedDocs = documents.filter((d) => selection.selectedIds.has(d.id));
    const docsWithElId = selectedDocs.filter((d) => d.el_doc_id);
    if (docsWithElId.length === 0) {
      const { toast } = await import("sonner");
      toast.error("Selected documents must be in the Knowledge Base before assigning to a genie");
      return;
    }
    await toggleDocuments(agent as Agent, docsWithElId, true);
    selection.deselectAll();
  };

  const handleBulkDelete = async () => {
    await bulkDelete(Array.from(selection.selectedIds));
    selection.deselectAll();
  };

  const handleBulkConsolidate = async (docName?: string, deleteOriginals?: boolean) => {
    await consolidate({
      documentIds: Array.from(selection.selectedIds),
      docName,
      prune: deleteOriginals,
    });
    selection.deselectAll();
  };

  const filteredOutsideWorkspaceDocuments = useMemo(() => {
    if (selectedGenieId === "all") return outsideWorkspaceDocuments;
    return (outsideWorkspaceDocuments || []).filter((doc) =>
      doc.dependent_agents?.some((a) => a.id === selectedGenieId),
    );
  }, [outsideWorkspaceDocuments, selectedGenieId]);

  const openDocumentDetails = (doc: DocumentWithMetadata, readOnly = false) => {
    setSelectedDocument(doc);
    setShowDocumentModal(true);
    setIsDetailsReadOnly(readOnly);
  };

  const closeDocumentDetails = () => {
    setShowDocumentModal(false);
    setSelectedDocument(null);
    setIsDetailsReadOnly(false);
  };

  useEffect(() => {
    if (!documentToOpen) return;
    setSelectedDocument(documentToOpen);
    setIsDetailsReadOnly(false);
    setShowDocumentModal(true);
    onDocumentOpened?.();
  }, [documentToOpen, onDocumentOpened]);

  useEffect(() => {
    if (!selectedDocument || !showDocumentModal) return;
    const updatedDoc = documents.find((doc) => doc.id === selectedDocument.id);
    if (updatedDoc && updatedDoc !== selectedDocument) {
      setSelectedDocument(updatedDoc);
    }
  }, [documents, selectedDocument, showDocumentModal]);

  const handleDeleteDocument = (id: string) => {
    setDeletingDocumentId(id);
    deleteDocument(id, {
      onSuccess: () => setDeletingDocumentId(null),
      onError: () => setDeletingDocumentId(null),
    });
  };

  const handleUpdateDocument = async (
    document: DocumentWithMetadata,
    updates: Partial<DocumentWithMetadata>,
  ) => {
    try {
      const updatedDoc = await updateDocument({ document, updates });
      if (selectedDocument?.id === document.id && updatedDoc) {
        setSelectedDocument(updatedDoc);
      }
    } catch (error) {
      console.error("Failed to update document:", error);
      throw error;
    }
  };

  const hasActiveFilters =
    selectedGenieId !== "all" ||
    selectedFolderId !== undefined ||
    selectedSourceType !== undefined;

  const handleClearFilters = () => {
    setSelectedGenieId("all");
    setSelectedFolderId(undefined);
    setSelectedSourceType(undefined);
  };

  const filterHeaderRight = (
    <div className="flex flex-wrap items-center gap-3">
      {clusterFilter && (
        <button
          type="button"
          onClick={onClearClusterFilter}
          className="inline-flex items-center gap-1.5 rounded-full border border-genie-primary/30 bg-genie-primary-light px-3 py-1 text-xs font-semibold text-genie-primary transition-colors hover:bg-white"
        >
          Topic: {clusterFilter.label}
          <X className="h-3 w-3" />
        </button>
      )}
      <DesktopFilterControls
        selectedGenieId={selectedGenieId}
        selectedSourceType={selectedSourceType}
        agents={agents}
        hasActiveFilters={hasActiveFilters}
        onGenieChange={setSelectedGenieId}
        onSourceTypeChange={setSelectedSourceType}
        onClear={handleClearFilters}
      />
      <div className="sm:hidden">
        <MobileFilterSheet
          selectedGenieId={selectedGenieId}
          selectedFolderId={selectedFolderId}
          selectedSourceType={selectedSourceType}
          agents={agents}
          hasActiveFilters={hasActiveFilters}
          onGenieChange={setSelectedGenieId}
          onFolderChange={setSelectedFolderId}
          onSourceTypeChange={setSelectedSourceType}
          onClear={handleClearFilters}
        />
      </div>
    </div>
  );

  return (
    <>
      <DocumentDetailsDrawer
        document={selectedDocument}
        isOpen={showDocumentModal}
        onClose={closeDocumentDetails}
        onSendToKB={sendToElevenLabs}
        onRemoveFromKB={removeFromKB}
        onDocumentUpdate={handleUpdateDocument}
        onDocumentDelete={handleDeleteDocument}
        readOnly={isDetailsReadOnly}
      />

      <div className="flex gap-6">
        {/* Folders Sidebar */}
        <div
          className={cn(
            "hidden lg:block flex-shrink-0 transition-[width] duration-200",
            isFoldersCollapsed ? "w-14" : "w-56 xl:w-64",
          )}
        >
          <div className="sticky top-6 rounded-xl border border-genie-border bg-white p-2 relative">
            {isFoldersCollapsed ? (
              <div className="flex flex-col items-center gap-2 py-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  title="Expand folders"
                  onClick={() => setIsFoldersCollapsed(false)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <span className="text-[11px] text-gray-600">Folders</span>
              </div>
            ) : (
              <>
                <button
                  className="absolute -right-2 -top-2 bg-white border border-genie-border rounded-full shadow-sm h-7 w-7 flex items-center justify-center"
                  aria-label="Collapse folders"
                  onClick={() => setIsFoldersCollapsed(true)}
                >
                  <ChevronLeft className="h-4 w-4 text-gray-600" />
                </button>
                <div className="p-2">
                  <FolderManagement
                    selectedFolderId={selectedFolderId}
                    onFolderSelect={setSelectedFolderId}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Breadcrumb Navigation for mobile */}
          <div className="mb-4 lg:hidden">
            <FolderNavigation
              selectedFolderId={selectedFolderId}
              onFolderSelect={setSelectedFolderId}
            />
          </div>

          <MyDocumentsSection
            isLoading={isLoading}
            documents={documents}
            openDocumentDetails={openDocumentDetails}
            handleDeleteDocument={handleDeleteDocument}
            handleSendToElevenLabs={sendToElevenLabs}
            headerRight={filterHeaderRight}
            deletingDocumentId={deletingDocumentId}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
            isSelected={selection.isSelected}
            onToggleSelect={selection.toggle}
            onSelectAll={selection.selectAll}
            onDeselectAll={selection.deselectAll}
            isAllSelected={selection.isAllSelected(documentIds)}
            isSomeSelected={selection.isSomeSelected(documentIds)}
            emptyHint={
              clusterFilter
                ? `Nothing from ${clusterFilter.label} on this page.`
                : searchTerm
                  ? "No documents match your search."
                  : undefined
            }
            onAddKnowledge={onRequestUpload}
          />

          <BulkActionToolbar
            selectedCount={selection.selectedCount}
            agents={agents}
            documents={documents}
            selectedIds={selection.selectedIds}
            onMoveToFolder={handleBulkMoveToFolder}
            onAssignToGenie={handleBulkAssignToGenie}
            onDelete={handleBulkDelete}
            onRemoveFromFolder={handleBulkRemoveFromFolder}
            onConsolidate={handleBulkConsolidate}
            onClearSelection={selection.deselectAll}
            isMoving={isFolderMoving || isBulkUpdating}
            isDeleting={isBulkDeleting}
            isConsolidating={isConsolidating}
          />

          <OutsideWorkspaceDocumentsSection
            isLoading={isLoadingOutsideWorkspaceDocuments}
            documents={filteredOutsideWorkspaceDocuments}
            openDocumentDetails={(doc) => openDocumentDetails(doc, true)}
          />
        </div>
      </div>
    </>
  );
};

export default KnowledgeBaseTab;
